import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import ExchangeWidget from '../components/ExchangeWidget';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import CurrencyIcon from '../components/CurrencyIcon';
import Footer from '../components/Footer';
import FiatRates from '../components/FiatRates';
import { FadeUp } from '../components/Motion';
import toast from 'react-hot-toast';
import api from '../utils/api';

const Exchange = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleExchangeSubmit = async (exchangeData) => {
    try {
      const response = await api.post('/exchange/request', exchangeData);
      const requestNumber = response.data?.request_number || Math.floor(1000000 + Math.random() * 9000000);
      toast.success('Demande créée avec succès !');
      navigate('/payment-confirmation', { state: { exchangeData, requestNumber, requestId: response.data?.id } });
    } catch (error) {
      console.error('Error creating exchange request:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la demande');
    }
  };

  const recentExchanges = [
    { fromSymbol: 'BTC', from: 'BTC', to: 'Virement SEPA', sent: '0.025', received: '2,450 EUR', time: 'Il y a 2 min' },
    { fromSymbol: 'ETH', from: 'ETH', to: 'Revolut', sent: '1.5', received: '5,130 EUR', time: 'Il y a 5 min' },
    { fromSymbol: 'USDT', from: 'USDT', to: 'Carte Visa', sent: '500', received: '460 EUR', time: 'Il y a 8 min' },
    { fromSymbol: 'EUR', from: 'Mastercard', to: 'BTC', sent: '1,000 EUR', received: '0.010', time: 'Il y a 12 min' },
    { fromSymbol: 'BNB', from: 'BNB', to: 'Virement SEPA', sent: '2.0', received: '1,220 EUR', time: 'Il y a 15 min' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F6F7]">
      <CryptoTicker />
      <Header />

      {/* Black hero band */}
      <div className="bg-black px-6 pt-14 pb-24">
        <div className="max-w-6xl mx-auto">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-white font-extrabold text-4xl md:text-6xl tracking-[-0.02em]" style={{ fontFamily: 'Manrope' }} data-testid="exchange-title">
            {t.exchangeWidget?.title || 'Échange Crypto & FIAT'}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="text-white/50 mt-3 text-lg">
            {t.exchangeWidget?.subtitle || 'Les meilleurs taux, transferts instantanés et taux bloqué 10 minutes'}
          </motion.p>
        </div>
      </div>

      <div className="px-6 pb-16 -mt-12" data-testid="exchange-container">
        <div className="max-w-6xl mx-auto">
          <ExchangeWidget onExchangeSubmit={handleExchangeSubmit} />

          <div className="mt-8"><FiatRates /></div>

          <FadeUp className="mt-16" data-testid="latest-exchanges">
            <h2 className="font-extrabold text-3xl text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.common?.recentExchanges || 'Derniers échanges'}</h2>
            <div className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">{t.common?.direction || 'Direction'}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">{t.common?.sent || 'Envoyé'}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">{t.common?.received || 'Reçu'}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">{t.common?.date || 'Date'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recentExchanges.map((exchange, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <CurrencyIcon symbol={exchange.fromSymbol} size={22} />
                            <span className="text-gray-900 font-semibold">{exchange.from}</span>
                            <ArrowRight className="w-4 h-4 text-gray-300" />
                            <span className="text-gray-600">{exchange.to}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900 font-mono-data">{exchange.sent}</td>
                        <td className="px-6 py-4 font-semibold text-emerald-600 font-mono-data">{exchange.received}</td>
                        <td className="px-6 py-4 text-gray-400 text-sm">{exchange.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeUp>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="trust-indicators">
            {[
              { val: '7%', label: t.common?.commission || 'Commission' },
              { val: '5-30 min', label: t.common?.exchangeTime || "Temps d'échange" },
              { val: '24/7', label: t.common?.supportAvailable || 'Support disponible' },
            ].map((item, idx) => (
              <FadeUp key={idx} delay={idx * 0.1} className="bg-black rounded-3xl p-8 text-center">
                <div className="text-white font-extrabold text-4xl mb-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>{item.val}</div>
                <div className="text-white/50">{item.label}</div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Exchange;
